import {
  Component,
  OnDestroy,
  OnInit,
  computed,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { PiiAnonymizationService } from '@maximizer/ai/data-access';
import { AiMarkupUtils, MarkdownRenderingPipe } from '@maximizer/ai/ui';
import {
  FeatureService,
  GlobalStore,
} from '@maximizer/core/shared/data-access';
import { EmailService } from '@maximizer/email-compose/data-access';
import { MenuOptionName } from '@maximizer/core/shared/ui/enums';
import {
  EntryType,
  EventAction,
  EventJourney,
  EventModule,
} from '@maximizer/outlook/shared/domain';
import { NoteService } from '@maximizer/outlook/note/data-access';
import { AddNoteComponent } from '@maximizer/outlook/note/ui';
import { AddTaskComponent } from '@maximizer/outlook/task/ui';
import { AddAppointmentComponent } from '@maximizer/outlook/appointment/ui';
import {
  AppointmentSeriesError,
  AppointmentService,
  EntryStore,
  NavigationService,
  OutlookService,
  TrackerService,
  seriesProgressParams,
} from '@maximizer/outlook/shared/data-access';
import { TaskService } from '@maximizer/outlook/task/data-access';
import {
  IqPromptSelection,
  isInsufficientInfoDraftResponse,
  OutlookIqBoostStore,
  OutlookIqBoostService,
  OutlookAiApiService,
  OutlookIqBoostPrivacyService,
  OutlookIqBoostCrmActionService,
  toCatalogPromptSelection,
} from '@maximizer/outlook/ai/data-access';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_LOADER } from '@progress/kendo-angular-indicators';
import { KENDO_DIALOG } from '@progress/kendo-angular-dialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UpperCasePipe } from '@angular/common';
import { Router } from '@angular/router';
import { catchError, finalize, forkJoin, of, switchMap, take } from 'rxjs';
import { OutlookIqBoostEntryContextComponent } from './entry-context/entry-context.component';
import { OutlookIqBoostPrivacySecurityComponent } from './privacy-security/privacy-security.component';
import { OutlookQuestionLibraryComponent } from './question-library/question-library.component';
import { OutlookIqBoostPromptInputComponent } from './prompt-input/prompt-input.component';
import { OutlookIqBoostChatComponent } from './chat/chat.component';
import { OutlookIqBoostSummaryComponent } from './summary/summary.component';
import {
  OUTLOOK_SHARED_UI,
  Tab,
  OutlookNotificationComponent,
} from '@maximizer/outlook/shared/ui';
import { OutlookAiResponseActionsComponent } from '../response-actions/response-actions.component';
import { OutlookAiMarkupUtils } from './utils/ai-markup-utils';
import { OutlookIqBoostSkeletonComponent } from './loaders';

const MAX_MAIN_PAGE_PROMPTS = 3;

@Component({
  selector: 'maximizer-outlook-iq-boost',
  templateUrl: './iq-boost.component.html',
  providers: [
    OutlookIqBoostPrivacyService,
    OutlookIqBoostCrmActionService,
    OutlookIqBoostStore,
    OutlookIqBoostService,
    OutlookAiApiService,
    NoteService,
    TaskService,
    EmailService,
    AppointmentService,
  ],
  imports: [
    OutlookIqBoostEntryContextComponent,
    OutlookIqBoostPromptInputComponent,
    OutlookIqBoostChatComponent,
    OutlookIqBoostSummaryComponent,
    TranslatePipe,
    UpperCasePipe,
    KENDO_BUTTON,
    KENDO_DIALOG,
    KENDO_LOADER,
    OUTLOOK_SHARED_UI,
    OutlookAiResponseActionsComponent,
    OutlookIqBoostSkeletonComponent,
    OutlookQuestionLibraryComponent,
    OutlookIqBoostPrivacySecurityComponent,
    AddNoteComponent,
    AddTaskComponent,
    AddAppointmentComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: `
    .question {
      @apply p-4 flex gap-2 items-center justify-between bg-bush-green-10 rounded hover:bg-bush-green-20 hover:cursor-pointer text-left;
    }
  `,
})
export class OutlookIqBoostComponent implements OnInit, OnDestroy {
  notification = viewChild<OutlookNotificationComponent>('notification');

  private readonly markdownPipe = new MarkdownRenderingPipe();
  readonly showQuestionLibrary = signal<boolean>(false);
  readonly showSaveNoteDialog = signal<boolean>(false);
  readonly showPrivacySecurityDialog = signal<boolean>(false);
  readonly savingNote = signal<boolean>(false);
  readonly savingCRMAction = signal<boolean>(false);
  readonly promptsLoading = signal<boolean>(true);
  readonly MenuOptionName = MenuOptionName;

  loading = computed(() => this.iqBoostStore.loading());

  recentPrompts = computed(() => this.iqBoostStore.recentPrompts());
  currentPage = computed(() => this.iqBoostStore.iqBoostPage());

  mainPagePrompts = computed<IqPromptSelection[]>(() => {
    const catalogPrompts = this.iqBoostStore
      .promptCategories()
      .flatMap((category) => category.prompts)
      .map(toCatalogPromptSelection);
    const topEmailAssistPrompt = this.iqBoostStore.emailAssistPrompts()[0];

    if (!topEmailAssistPrompt) {
      return catalogPrompts.slice(0, MAX_MAIN_PAGE_PROMPTS);
    }
    return [
      ...catalogPrompts.slice(0, MAX_MAIN_PAGE_PROMPTS - 1),
      topEmailAssistPrompt,
    ];
  });

  saveNoteDisabled = computed(
    () =>
      this.loading() ||
      !this.iqBoostStore.summaryResponse() ||
      this.savingNote(),
  );

  iqBoostFeature = computed(() => {
    const page = this.iqBoostStore.iqBoostPage();
    if (page === 'summary') return 'summary';
    if (page === 'chat') return 'chat';
    return null;
  });

  copyContent = computed(() => {
    const page = this.iqBoostStore.iqBoostPage();
    if (page === 'summary') {
      return this.cleanForClipboard(
        this.markdownPipe.transform(this.iqBoostStore.summaryResponse()),
      );
    }
    if (page === 'chat') {
      const history = this.iqBoostStore.chatHistory();
      if (!history.length) return null;
      return history
        .map((message) =>
          this.cleanForClipboard(this.markdownPipe.transform(message.content)),
        )
        .join('<br><br>');
    }
    return null;
  });

  feedbackGiven = computed(() => {
    const page = this.iqBoostStore.iqBoostPage();
    return page === 'chat'
      ? this.iqBoostStore.chatFeedbackGiven()
      : this.iqBoostStore.summaryFeedbackGiven();
  });

  showCreateEmail = computed(
    () =>
      this.iqBoostStore.iqBoostPage() === 'summary' &&
      (this.iqBoostStore.currentPromptSelection()?.isDraft ?? false),
  );

  createEmailDisabled = computed(() =>
    isInsufficientInfoDraftResponse(this.iqBoostStore.summaryResponse()),
  );

  protected get isComposeMode(): boolean {
    return !!this.outlookService.isCompose;
  }

  copySuccessKey = computed(() => {
    const page = this.iqBoostStore.iqBoostPage();
    if (page === 'chat') return 'outlook.aiResponseActions.chatCopySuccess';
    if (page === 'summary')
      return 'outlook.aiResponseActions.summaryCopySuccess';
    return 'outlook.aiResponseActions.copySuccess';
  });

  tabs: Tab[] = [
    {
      id: 'summary',
      label: this.translate.instant('outlook.iqBoost.suggestedQuestions'),
      iconClass: 'fa-icons icon-book-sparkles icon-14',
    },
    {
      id: 'chat',
      label: this.translate.instant('outlook.iqBoost.askTitle'),
      iconClass: 'fa-icons icon-message-captions icon-14',
    },
  ];

  constructor(
    private readonly navigationService: NavigationService,
    private readonly translate: TranslateService,
    private readonly iqBoostService: OutlookIqBoostService,
    private readonly noteService: NoteService,
    private readonly entryStore: EntryStore,
    private readonly globalStore: GlobalStore,
    private readonly tracker: TrackerService,
    public iqBoostStore: OutlookIqBoostStore,
    private readonly router: Router,
    private readonly aiApi: OutlookAiApiService,
    readonly privacyService: OutlookIqBoostPrivacyService,
    readonly crmPrefillService: OutlookIqBoostCrmActionService,
    private readonly outlookService: OutlookService,
    private readonly feature: FeatureService,
  ) {}

  ngOnInit(): void {
    if (!this.entryStore.entry()) {
      this.router.navigate(['/']);
      return;
    }

    this.tracker.trackEvent({
      module: EventModule.IqBoost,
      journey: EventJourney.Action,
      action: EventAction.Opened,
      metadata: { entry: this.entryStore.type() },
    });

    const emailAssistEnabled =
      !this.outlookService.isCalendarItem &&
      this.feature.isFeatureOn(
        'integration-o365-ai-iq-boost-email-assist-enabled',
        false,
      );

    // Hold the skeleton until both prompt sources settle, so the top
    // email-assist prompt doesn't swap into an already-rendered list.
    forkJoin([
      this.iqBoostService.loadPromptCatalog().pipe(take(1)),
      emailAssistEnabled
        ? this.iqBoostService.loadEmailAssistPrompts().pipe(take(1))
        : of(null),
    ]).subscribe(() => this.promptsLoading.set(false));

    this.iqBoostService
      .loadAdminPrivacySettings()
      .pipe(
        catchError(() => of(void 0)),
        switchMap(() => this.iqBoostService.loadRecentPrompts()),
        take(1),
      )
      .subscribe();
    this.iqBoostService
      .loadFirstTimeLaunch()
      .pipe(take(1))
      .subscribe((isFirstTime) => {
        if (isFirstTime) this.showPrivacySecurityDialog.set(true);
      });

    this.setBackOverrideForCurrentPage();
  }

  onPrivacySecurityClose(): void {
    this.showPrivacySecurityDialog.set(false);
    this.iqBoostStore.setFirstTimeLaunch(false);
  }

  ngOnDestroy(): void {
    this.clearBackOverride();
  }

  openQuestionLibrary(): void {
    this.showQuestionLibrary.set(true);
  }

  closeQuestionLibrary(): void {
    this.showQuestionLibrary.set(false);
  }

  handleLibraryPromptSelected(prompt: IqPromptSelection): void {
    this.closeQuestionLibrary();
    this.handlePredefinedPromptClick(prompt);
  }

  handlePromptSubmit(prompt: string): void {
    if (prompt.trim().length > 0) {
      this.iqBoostStore.setChatPrompt(prompt);
      this.iqBoostStore.clearChatResponse();
      this.iqBoostService.pushRecentPrompt(prompt);
      this.startPlaceholderTransition('chat');
    }
  }

  handlePredefinedPromptClick(prompt: IqPromptSelection): void {
    this.iqBoostStore.clearSummaryResponse();
    this.iqBoostStore.setCurrentPrompt(prompt.title);
    this.iqBoostStore.setCurrentPromptSelection(prompt);
    // Web Access doesn't support Email Assist prompts; skip adding them to recent prompts.
    if (!prompt.isEmailAssist) {
      this.iqBoostService.pushRecentPrompt(prompt.title);
    }
    this.startPlaceholderTransition('summary');
  }

  private startPlaceholderTransition(nextPage: 'chat' | 'summary'): void {
    if (nextPage === 'chat' && this.iqBoostStore.iqBoostPage() !== 'chat') {
      this.iqBoostStore.clearChatHistory();
    }
    this.iqBoostStore.setIqBoostPage(nextPage);
    this.clearBackOverride();
    this.setBackOverrideForCurrentPage();
  }

  private setBackOverrideForCurrentPage(): void {
    if (this.iqBoostStore.iqBoostPage() === 'main') {
      this.clearBackOverride();
      return;
    }

    this.navigationService.backNavigationOverride.set({
      state: true,
      pathSegments: [],
      onBack: () => {
        this.iqBoostStore.setIqBoostPage('main');
        this.iqBoostStore.clearChatHistory();
        this.iqBoostStore.clearChatPrompt();
      },
    });
  }

  private clearBackOverride(): void {
    this.navigationService.backNavigationOverride.set({
      state: false,
      pathSegments: [],
    });
  }

  onSaveNote(): void {
    this.tracker.trackEvent({
      module: EventModule.IqBoost,
      journey: EventJourney.SaveNote,
      action: EventAction.Clicked,
    });

    this.showSaveNoteDialog.set(true);
  }

  async onCreateEmail(): Promise<void> {
    const body = this.copyContent();
    if (!body) return;

    if (this.outlookService.isCompose) {
      const inserted = await this.outlookService.prependToEmailBodyAsync(body);
      if (!inserted) {
        this.notification()?.showMessage(
          'error',
          'outlook.aiResponseActions.createEmailFailed',
          true,
        );
      }
      return;
    }

    const subject = await this.outlookService.getSubject(true);
    const { to, cc } = this.outlookService.getReadReplyRecipients();

    const created = this.outlookService.createNewMessage({
      toRecipients: to,
      ccRecipients: cc,
      subject: `RE: ${subject}`.trim(),
      htmlBody: body,
    });
    if (!created) {
      this.notification()?.showMessage(
        'error',
        'outlook.aiResponseActions.createEmailFailed',
        true,
      );
    }
  }

  confirmSaveNote(confirmed: boolean): void {
    this.showSaveNoteDialog.set(false);
    if (!confirmed) return;

    const entry = this.entryStore.entry();
    const type = this.entryStore.type();
    const entryKey = entry?.key ?? null;

    this.savingNote.set(true);
    this.noteService
      .create(
        this.buildNoteRequest(
          this.cleanForClipboard(
            this.markdownPipe.transform(
              this.iqBoostStore.summaryResponse() ?? '',
            ),
          ),
          entryKey,
          type,
        ),
      )
      .pipe(
        catchError(() => {
          this.notification()?.showMessage(
            'error',
            'outlook.iqBoost.saveNoteDialog.error',
            true,
          );
          return of(null);
        }),
        finalize(() => this.savingNote.set(false)),
      )
      .subscribe((result) => {
        if (result == null) return;
        this.notification()?.showMessage(
          'success',
          'outlook.iqBoost.saveNoteDialog.success',
          true,
        );
      });
  }

  onFeedbackVoted(): void {
    const page = this.iqBoostStore.iqBoostPage();
    if (page === 'chat') {
      this.iqBoostStore.setChatFeedbackGiven(true);
    } else {
      this.iqBoostStore.setSummaryFeedbackGiven(true);
    }
  }

  // Closing the dialog mid-save would orphan the create: rows get created, the result is never shown.
  cancelCRMAction(): void {
    if (this.savingCRMAction()) return;
    this.iqBoostStore.clearConfirmCreateCRMAction();
  }

  onEmbeddedCRMActionSavingChange(saving: boolean): void {
    this.savingCRMAction.set(saving);
  }

  onEmbeddedCRMActionSaved(action: MenuOptionName): void {
    this.iqBoostStore.clearConfirmCreateCRMAction();

    let successKey: string;
    let journey: EventJourney;
    if (action === MenuOptionName.AddNote) {
      successKey = 'outlook.iqBoost.saveNoteDialog.success';
      journey = EventJourney.SaveNote;
    } else if (action === MenuOptionName.ScheduleToDo) {
      successKey = 'outlook.iqBoost.crmAction.todoSuccess';
      journey = EventJourney.SaveNote;
    } else if (action === MenuOptionName.ScheduleMeeting) {
      successKey = 'outlook.iqBoost.crmAction.meetingSuccess';
      journey = EventJourney.SaveNote;
    } else {
      return;
    }

    this.notification()?.showMessage('success', successKey, true);

    this.tracker.trackEvent({
      module: EventModule.IqBoost,
      journey,
      action: EventAction.Clicked,
    });
  }

  onEmbeddedAppointmentPartiallySaved(error: AppointmentSeriesError): void {
    this.iqBoostStore.clearConfirmCreateCRMAction();
    this.notification()?.showMessage(
      'error',
      this.translate.instant(
        'outlook.appointment.recurrence.partialError',
        seriesProgressParams(error.progress),
      ),
    );
  }

  onEmbeddedCRMActionCancelled(): void {
    this.cancelCRMAction();
  }

  createCRMAction(): void {
    this.cancelCRMAction();
  }

  private cleanForClipboard(html: string): string {
    const withoutAnonymizedTags = AiMarkupUtils.replaceAnonymizedTags(
      html,
      (value) => value,
    );
    const deanonymized = PiiAnonymizationService.processPiiMaskTokensInHtml(
      withoutAnonymizedTags,
      (type, number) =>
        this.aiApi.deAnonymize(`${type}${number}`) ?? `${type}${number}`,
    );
    const withoutMaximizer = OutlookAiMarkupUtils.replaceMaximizerReferences(
      deanonymized,
      () => null,
    );
    const withoutCrm = OutlookAiMarkupUtils.replaceCRMObjects(
      withoutMaximizer,
      () => '',
    );
    return OutlookAiMarkupUtils.stripEntityKeyRefs(withoutCrm);
  }

  private buildNoteRequest(
    richText: string,
    entryKey: string | null,
    type: string | undefined,
  ) {
    return {
      key: null,
      parentKey: entryKey,
      dateTime: new Date(),
      richText,
      creator: this.globalStore.session()?.user.id ?? null,
      category: 'AI-Generated',
      important: false,
      type: 0,
      abEntryKey: type === EntryType.AbEntry ? entryKey : null,
      leadKey: type === EntryType.Lead ? entryKey : null,
    };
  }

  handleTabChange(id: string): void {
    if (id === 'chat') {
      this.iqBoostStore.setIqBoostPage('chat');
    } else if (id === 'summary') {
      this.iqBoostStore.setIqBoostPage('summary');
    }
  }
}
